import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase';

const EventContext = createContext();

export const useEvents = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvents must be used within an EventProvider');
  }
  return context;
};

const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadEvents = async () => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured');
        return;
      }

      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          artists:event_artists(
            artist:artists(*)
          )
        `)
        .order('date', { ascending: false });

      if (error) throw error;

      // Transform the data to match frontend expectations
      const transformedEvents = data.map(event => ({
        ...event,
        // Normalize date to YYYY-MM-DD for UI forms
        date: (typeof event.date === 'string' && event.date.includes('T'))
          ? event.date.split('T')[0]
          : event.date,
        artists: event.artists?.map(ea => ({
          id: ea.artist.id,
          name: ea.artist.name,
          // Note: type may not exist in DB; keep if present but we don't depend on it
          type: ea.artist.type || 'DJ',
          bio: ea.artist.bio,
          spotify: ea.artist.social_links?.spotify,
          soundcloud: ea.artist.social_links?.soundcloud,
          website: ea.artist.social_links?.website
        })) || []
      }));

      setEvents(transformedEvents);
      console.log('✅ Events loaded successfully:', transformedEvents);

    } catch (err) {
      console.error('❌ Failed to load events from Supabase:', err);
      setError(err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const addEvent = async (newEvent) => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured');
        return;
      }

      setError(null);
      console.log('📝 Adding new event:', newEvent);

      // Create the event data without artists
      const genreString = Array.isArray(newEvent.genres) 
        ? newEvent.genres.join(', ') 
        : (newEvent.genre || '');
      const normalizedDate = (newEvent.date && typeof newEvent.date === 'string')
        ? newEvent.date.split('T')[0]
        : newEvent.date;
        
      const eventWithId = {
        id: newEvent.id || Date.now(),
        title: (newEvent.title || '').substring(0, 100),
        description: newEvent.description,
        date: normalizedDate || null,
        time: newEvent.time,
        venue: (newEvent.venue || '').substring(0, 100),
        address: newEvent.address,
        genre: genreString.substring(0, 100),
        price_range: newEvent.ticketPrice || newEvent.price || 'Free',
        image_url: newEvent.image,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('events')
        .insert(eventWithId)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Event created with ID:', data.id);
      
      // Handle artists (upsert + link)
      if (newEvent.artists && newEvent.artists.length > 0) {
        const seenArtistIds = new Set();
        for (const artist of newEvent.artists) {
          if (!artist?.name || !artist.name.trim()) continue;

          // Coerce possible numeric strings
          const parsedId = typeof artist.id === 'string' && /^\d+$/.test(artist.id) ? Number(artist.id) : artist.id;
          const parsedExistingId = typeof artist.existingId === 'string' && /^\d+$/.test(artist.existingId) ? Number(artist.existingId) : artist.existingId;
          const hasNumericId = parsedId && typeof parsedId === 'number';
          const existingId = parsedExistingId && typeof parsedExistingId === 'number' ? parsedExistingId : null;

          let artistData;
          if (hasNumericId || existingId) {
            // Update existing artist with any provided fields
            const { data: updatedArtist, error: updateError } = await supabase
              .from('artists')
              .update({
                name: artist.name,
                bio: artist.bio || '',
                type: artist.type || 'DJ', // Only if your DB supports this column
                social_links: {
                  website: artist.socialLinks?.website ?? '',
                  instagram: artist.socialLinks?.instagram ?? '',
                  facebook: artist.socialLinks?.facebook ?? '',
                  twitter: artist.socialLinks?.twitter ?? '',
                  spotify: artist.socialLinks?.spotify ?? '',
                  soundcloud: artist.socialLinks?.soundcloud ?? '',
                  bandcamp: artist.socialLinks?.bandcamp ?? '',
                  youtube: artist.socialLinks?.youtube ?? ''
                }
              })
              .eq('id', hasNumericId ? parsedId : existingId)
              .select()
              .single();

            if (updateError) {
              console.error('❌ Failed to update existing artist:', updateError);
              throw updateError;
            }
            artistData = updatedArtist;
          } else {
            // Create new artist
            const { data: createdArtist, error: createError } = await supabase
              .from('artists')
              .insert({
                name: artist.name,
                bio: artist.bio || '',
                type: artist.type || 'DJ', // Only if your DB supports this column
                social_links: {
                  website: artist.socialLinks?.website ?? '',
                  instagram: artist.socialLinks?.instagram ?? '',
                  facebook: artist.socialLinks?.facebook ?? '',
                  twitter: artist.socialLinks?.twitter ?? '',
                  spotify: artist.socialLinks?.spotify ?? '',
                  soundcloud: artist.socialLinks?.soundcloud ?? '',
                  bandcamp: artist.socialLinks?.bandcamp ?? '',
                  youtube: artist.socialLinks?.youtube ?? ''
                }
              })
              .select()
              .single();

            if (createError) {
              console.error('❌ Failed to create artist:', createError);
              throw createError;
            }
            artistData = createdArtist;
          }

          // Link artist to event using the actual event ID from database (dedupe per event)
          if (seenArtistIds.has(artistData.id)) {
            console.log('ℹ️ Skipping duplicate artist link for id', artistData.id);
            continue;
          }
          seenArtistIds.add(artistData.id);
          const { error: linkError } = await supabase
            .from('event_artists')
            .insert({
              event_id: data.id,
              artist_id: artistData.id
            });

          if (linkError) {
            console.error('❌ Failed to link artist:', linkError);
            throw linkError;
          }
        }
      }

      await loadEvents();
      return data;

    } catch (err) {
      console.error('💥 Failed to add event to Supabase:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateEvent = async (eventId, updatedEventData) => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured');
        return;
      }

      console.log('🔍 UpdateEvent received data:', updatedEventData);

      // Handle different possible field names and formats
      const genreString = Array.isArray(updatedEventData.genres) 
        ? updatedEventData.genres.join(', ') 
        : (updatedEventData.genre || updatedEventData.genres || '');
      
      const normalizedUpdateDate = (updatedEventData.date && typeof updatedEventData.date === 'string')
        ? updatedEventData.date.split('T')[0]
        : updatedEventData.date;

      const updateData = {
        title: (updatedEventData.title || '').substring(0, 100),
        description: updatedEventData.description,
        date: normalizedUpdateDate,
        time: updatedEventData.time,
        venue: (updatedEventData.venue || '').substring(0, 100),
        address: updatedEventData.address,
        genre: genreString.substring(0, 100),
        price_range: updatedEventData.ticketPrice !== undefined ? updatedEventData.ticketPrice :
                     updatedEventData.price_range !== undefined ? updatedEventData.price_range :
                     updatedEventData.price !== undefined ? updatedEventData.price : 'Free',
        image_url: updatedEventData.image_url || updatedEventData.image,
        updated_at: new Date().toISOString()
      };

  // Remove any undefined values to avoid database issues
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
  // Specifically avoid writing empty string to a date column
  if (updateData.date === '') delete updateData.date;

      console.log('🔍 Mapped updateData:', updateData);

      const { data, error } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', eventId)
        .select()
        .single();

      if (error) throw error;

      // Handle artists update
      if (updatedEventData.artists) {
        console.log('🎭 Updating artists:', updatedEventData.artists);
        
        // Always reset existing relationships so we can re-link current list
        const { error: clearLinksError } = await supabase
          .from('event_artists')
          .delete()
          .eq('event_id', eventId);
        if (clearLinksError) {
          console.warn('⚠️ Failed clearing old event_artists links:', clearLinksError);
        }

  // If artist array is empty, we're done (cleared links above)
        if (!updatedEventData.artists.length) {
          await loadEvents();
          console.log('✅ Event updated (artists cleared) in Supabase:', data);
          return data;
        }

  // Add/update artists
  const seenArtistIds = new Set();
  for (const artist of updatedEventData.artists) {
          if (artist.name && artist.name.trim()) {
            // Check if artist exists or create new one
            let artistData;
      // Coerce possible numeric strings to numbers
      const parsedId = typeof artist.id === 'string' && /^\d+$/.test(artist.id) ? Number(artist.id) : artist.id;
      const parsedExistingId = typeof artist.existingId === 'string' && /^\d+$/.test(artist.existingId) ? Number(artist.existingId) : artist.existingId;
      const hasNumericId = parsedId && typeof parsedId === 'number';
      const existingId = parsedExistingId && typeof parsedExistingId === 'number' ? parsedExistingId : null;
            if (hasNumericId || existingId) {
              // Update existing artist
              const { data: updatedArtist, error: updateError } = await supabase
                .from('artists')
                .update({
                  name: artist.name,
                  bio: artist.bio || '',
                  type: artist.type || 'DJ', // Only if your DB supports this column
                  social_links: {
                    website: artist.socialLinks?.website ?? '',
                    instagram: artist.socialLinks?.instagram ?? '',
                    facebook: artist.socialLinks?.facebook ?? '',
                    twitter: artist.socialLinks?.twitter ?? '',
                    spotify: artist.socialLinks?.spotify ?? '',
                    soundcloud: artist.socialLinks?.soundcloud ?? '',
                    bandcamp: artist.socialLinks?.bandcamp ?? '',
                    youtube: artist.socialLinks?.youtube ?? ''
                  }
                })
        .eq('id', hasNumericId ? parsedId : existingId)
                .select()
                .single();
              
              if (updateError) {
                console.error('❌ Failed to update artist:', updateError);
                throw updateError;
              }
              artistData = updatedArtist;
            } else {
              // Create new artist
              const { data: newArtist, error: createError } = await supabase
                .from('artists')
                .insert({
                  name: artist.name,
                  bio: artist.bio || '',
                  type: artist.type || 'DJ', // Only if your DB supports this column
                  social_links: {
                    website: artist.socialLinks?.website ?? '',
                    instagram: artist.socialLinks?.instagram ?? '',
                    facebook: artist.socialLinks?.facebook ?? '',
                    twitter: artist.socialLinks?.twitter ?? '',
                    spotify: artist.socialLinks?.spotify ?? '',
                    soundcloud: artist.socialLinks?.soundcloud ?? '',
                    bandcamp: artist.socialLinks?.bandcamp ?? '',
                    youtube: artist.socialLinks?.youtube ?? ''
                  }
                })
                .select()
                .single();
              
              if (createError) {
                console.error('❌ Failed to create artist:', createError);
                throw createError;
              }
              artistData = newArtist;
            }

            // Link artist to event (dedupe per event)
            if (seenArtistIds.has(artistData.id)) {
              console.log('ℹ️ Skipping duplicate artist link for id', artistData.id);
              continue;
            }
            seenArtistIds.add(artistData.id);
            const { error: linkError } = await supabase
              .from('event_artists')
              .insert({
                event_id: eventId,
                artist_id: artistData.id
              });
              
            if (linkError) {
              console.error('❌ Failed to link artist:', linkError);
              throw linkError;
            }
          }
        }
      }

      // Reload events to get the updated list
      await loadEvents();
      
      console.log('✅ Event updated in Supabase:', data);
      return data;

    } catch (err) {
      console.error('❌ Failed to update event in Supabase:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured');
        return;
      }

      // Get the event first to check for image cleanup
      const { data: eventData, error: fetchError } = await supabase
        .from('events')
        .select('image_url')
        .eq('id', eventId)
        .single();

      // Delete from artists relationships first (foreign key constraint)
      await supabase
        .from('event_artists')
        .delete()
        .eq('event_id', eventId);

      // Delete the event itself
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      // Clean up image if it exists and is from Supabase storage
      if (eventData?.image_url && eventData.image_url.includes('supabase')) {
        try {
          const urlParts = eventData.image_url.split('/');
          const fileName = urlParts[urlParts.length - 1];
          const { error: deleteError } = await supabase.storage
            .from('event-images')
            .remove([fileName]);
          
          if (deleteError) {
            console.warn('Failed to delete image file:', deleteError);
          }
        } catch (cleanupError) {
          console.warn('Image cleanup failed:', cleanupError);
        }
      }

      await loadEvents();
      console.log('✅ Event deleted successfully');

    } catch (err) {
      console.error('❌ Failed to delete event:', err);
      setError(err.message);
      throw err;
    }
  };

  const value = {
    events,
    loading,
    error,
    addEvent,
    updateEvent,
    deleteEvent,
    loadEvents
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};

export { EventProvider };
export default EventProvider;
